<template>
    <div>
        <vx-client-side-table
            v-bind="attrs"
            v-on="$listeners"
            ref="table"
            :value="localValue"
            :sst="true"
            :class="{ loading: isLoadingActiveSet }"
            :data="data"
            :total-records="activeDataSet.count"
            :id-key="getKey"
            class="server-side-table flex flex-col w-full"
            paginate
            :page-size.sync="pageSize"
            :sort-params.sync="sortParams"
            :current-page.sync="currentPage"
            :search-text="searchText"
            @update:search-text="onSearch"
        >
            <template slot="csv">
                <csv-button
                    v-if="hasFetchCsv"
                    :endpoint="fetchCsv"
                    :filters="parameters"
                    class="mr-3"
                    :current-results="activeDataSet.data"
                    :total-results="activeDataSet.count"
                />
            </template>
            <slot v-for="(_, name) in $slots" :name="name" :slot="name" />
            <template v-for="(_, name) in $scopedSlots" v-slot:[name]="slotData">
                <slot :name="name" v-bind="slotData" />
            </template>
        </vx-client-side-table>
    </div>
</template>

<script>
import Vue from 'vue'
import CsvButton from '../../../components/generic/VxServerSideTable/CsvButton.vue'

/*
example column object:
    {
        title: 'Customer',                              // The heading title
        sort: 'account.name',                           // The sort id which is passed to the backend
        value: 'customer',                              // A way to access the display value for the cell, can be a function or key (use . to parse an object tree ie. bdm.name)
        visible: true,                                  // Reactively show/hide the cell (defaults to true)
        style: 'width: 50%; color: green;'              // Styles to apply to the cell
        class: 'my-class flex flex-col'                 // Classes to apply to the cell
        format: (row) => row.date.toHyphenatedDateString()     // Function which receives the value and further formats it
        slotName: 'FullName'                            // Optional slot name when title has spaces (or is different). Defaults to the title if not supplied
    },
*/
export default {
    name: 'vx-server-side-table',
    components: { CsvButton },
    props: {
        value: {
            type: Object
        },
        idKey: {
            type: [Function, String],
            default: 'id'
        },
        fixedPageSize: {
            type: [Number, String],
            required: false
        },
        filters: {
            type: Object,
            default: () => {}
        },
        fetchData: {
            type: Function,
            required: true
        },
        fetchCsv: {
            type: Function,
            required: false
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            sortParams: {
                sortKey: null,
                sortType: null
            },
            loadingCsv: false,
            onActiveRoute: true,
            searchTimeout: null,
            currentPage: 1,
            searchText: null,
            sortKey: null,
            sortType: null,
            pageSize: 15,
            typingDelay: 1000,
            parameterHash: 'initial',
            pageSizeOptions: [
                { text: '10', value: 10 },
                { text: '15', value: 15 },
                { text: '50', value: 50 },
                { text: '100', value: 100 },
                { text: '200', value: 200 }
            ],
            cachedData: {
                initial: {
                    error: false,
                    loading: false,
                    pages: 1,
                    count: 0,
                    data: []
                }
            }
        }
    },
    watch: {
        parameters: {
            handler: function (val, oldV) {
                // next tick to allow disabled flag to initalize
                this.$nextTick(() => {
                    this.getData(val)
                })
            },
            immediate: true,
            deep: true
        },
        pageResetParameters: {
            handler: function (val, oldV) {
                this.currentPage = 1
            },
            deep: true
        },
        isLoadingActiveSet: {
            handler: function (val) {
                this.setTableDataLoadSpinner(val)
            },
            immediate: true
        },
        isActive(newV, oldV) {
            if (newV === true) {
                this.getData(this.parameters)
            }
        },
        data: {
            handler: function (newV) {
                this.$emit('page-results-changed', newV)
            },
            deep: true
        }
    },
    computed: {
        localValue: {
            get() {
                return this.value
            },
            set(val) {
                this.$emit('input', val)
            }
        },
        hasFetchCsv() {
            return !!this.fetchCsv
        },
        isActive() {
            return this.onActiveRoute && !this.disabled
        },
        pageSize_x() {
            return this.fixedPageSize || this.pageSize
        },
        activeDataSet() {
            return this.cachedData[this.parameterHash]
        },
        getKey() {
            if (!this.idKey) {
                return (row, index) => index + this.currentPage * this.pageSize_x
            }
            if (typeof this.idKey === 'function') {
                return this.idKey
            }
            return (row) => row && row[this.idKey]
        },
        data() {
            return this.activeDataSet.data
        },
        totalPages() {
            return this.activeDataSet.pages || 1
        },
        localFilters() {
            return {
                search: this.searchText,
                ...this.filters
            }
        },
        pageResetParameters() {
            return {
                ...this.localFilters,
                pageSize: this.pageSize_x
            }
        },
        parameters() {
            return {
                ...this.pageResetParameters,
                sortKey: this.sortParams.sortKey || this.pageResetParameters.sortKey,
                sortType: this.sortParams.sortType || this.pageResetParameters.sortType,
                pageNo: this.currentPage - 1 // 0 indexed on server side
            }
        },
        isLoadingActiveSet() {
            return !this.disabled && this.activeDataSet.loading
        },
        attrs() {
            var result = { ...this.$attrs, disabled: this.disabled }
            return result
        }
    },
    methods: {
        setTableDataLoadSpinner(show, type) {
            // if not done in nextTick, this selects a dom element before it has been added to the dom
            // this may be nothing or it may be the prior route.
            this.$nextTick(() => {
                var el = this.$el.querySelector('.vs-con-tbody')
                if (!el) {
                    return
                }
                if (show) {
                    this.$vs.loading({
                        container: el,
                        type: type || 'default'
                    })
                    return
                }
                this.$vs.loading.close(el)
            })
        },
        reset() {
            this.parameterHash = 'initial'
            this.cachedData = {
                initial: {
                    error: false,
                    loading: true,
                    pages: 1,
                    count: 0,
                    data: []
                }
            }
            this.getData(this.parameters)
        },
        resetCurrentPage() {
            var keptCaches = ['initial', this.parameterHash]
            Object.keys(this.cachedData).forEach((cacheKey) => {
                if (!keptCaches.contains(cacheKey)) {
                    delete this.cachedData[cacheKey]
                }
            })
            this.getData(this.parameters, true)
        },
        updateRecord(record) {
            var recordId = this.getKey(record)
            // iterate through each set of cached search data
            // merge the new record into any which share the updated record's id
            Object.keys(this.cachedData).forEach((cacheKey) => {
                this.cachedData[cacheKey].data.forEach((cachedRecord) => {
                    if (this.getKey(cachedRecord) === recordId) {
                        Object.assign(cachedRecord, record)
                    }
                })
            })
        },
        async removeRecord(record) {
            var activeIndex = -1
            if (this.localValue && this.getKey(record) == this.getKey(this.localValue)) {
                activeIndex = this.activeDataSet.data.indexOf(this.localValue)
            }
            // iterate through each set of cached search data
            Object.keys(this.cachedData).forEach((cacheKey) => {
                if (cacheKey != 'initial' && cacheKey != this.parameterHash) {
                    delete this.cachedData[cacheKey]
                }
            })

            await this.getData(this.parameters, true)
            setTimeout(
                function () {
                    if (activeIndex == -1) {
                        return
                    }
                    if (!this.activeDataSet.data.any()) {
                        this.$emit('input', null)
                    }
                    var val = this.activeDataSet.data[activeIndex]
                    if (!val) {
                        val = this.activeDataSet.data[activeIndex - 1]
                    }
                    this.$emit('input', val)
                }.bind(this),
                200
            )
        },
        async generateHash(parameters) {
            return await Object.hash(parameters)
        },
        onSearch(searchText) {
            if (this.searchTimeout != null) {
                clearTimeout(this.searchTimeout)
                this.searchTimeout = null
            }
            this.setTableDataLoadSpinner(true, 'sound')
            this.searchTimeout = setTimeout(
                function () {
                    this.searchTimeout = null
                    // If a search handler is bound, allow it to handle
                    // the filter. This allows multiple components to be bound
                    // to the same filter set at a higher level (eg: summaries)
                    if (this.$listeners.search && this.isActive) {
                        this.$emit('search', searchText)
                    }
                    this.searchText = searchText || ''
                    this.setTableDataLoadSpinner(false)
                }.bind(this),
                this.typingDelay
            )
        },
        retrieveCache(hash) {
            return this.cachedData[hash]
        },
        async getData(params, overwrite) {
            if (!this.isActive) {
                return
            }
            var hash = await this.generateHash(params)
            if (!overwrite && this.retrieveCache(hash)) {
                this.parameterHash = hash
                return
            }
            Vue.set(this.cachedData, hash, {
                error: false,
                loading: true,
                count: this.activeDataSet.count,
                pages: this.activeDataSet.pages,
                data: this.setIndexX(this.activeDataSet.data)
            })
            this.parameterHash = hash
            try {
                var response = this.fetchData(params)
                // check if function was async
                if (response.then) {
                    response = await response
                }
                Vue.set(this.cachedData, hash, {
                    error: false,
                    count: response.totalCount,
                    pages: response.totalPages,
                    data: this.setIndexX(response.data)
                })
            } catch (err) {
                this.cachedData[hash].error = err
                this.notifyServerError(err)
            } finally {
                this.cachedData[hash].loading = false
            }
        },
        //Test : Usecase to add xindex on each row alike with client-side table functionality for linedetail purpose.
        setIndexX(data) {
            if (data.length === 0) {
                return []
            }
            return data.map(function (item, index) {
                return { ...item, xIndex: index + 1 }
            })
        }
    },
    activated() {
        // isActive is defaulted to true, so this will only run when the component has been kept alive
        if (!this.onActiveRoute) {
            this.onActiveRoute = true
        }
    },
    deactivated() {
        this.onActiveRoute = false
    }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/vuexy/_variables.scss';

.server-side-table {
    height: calc(100% - 54px);

    &.loading {
        ::v-deep .vs-table--tbody {
            min-height: 200px;
        }
    }
}
</style>
